import { MessageDescriptor } from 'react-intl'

import { Programs } from './programConfigs'
import { Episode, ProgramNames, ProviderProgramTaxonomy } from '../../models'
import { TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'

export function getProgramNameFromId(programId?: string | null): ProgramNames | undefined {
  return programId ? Programs.getProgramConfigById(programId)?.programName : undefined
}

export function getProgramIdFromName(programName?: string): string | undefined {
  return Programs.getProgramConfigByName(programName)?.blendedCareProgramId ?? undefined
}

export function isCoachingProgram(name: ProgramNames | string): boolean {
  return Programs.getProgramConfigByName(name)?.isCoachingProgram ?? false
}

export function isDAEngageProgram(name: ProgramNames | string): boolean {
  return Programs.getProgramConfigByName(name)?.isDAEngageProgram ?? false
}

export function isDAMinorProgram(name: ProgramNames | string): boolean {
  return Programs.getProgramConfigByName(name)?.isDAMinorProgram ?? false
}

export type UserConsent = {
  programName: string
  consentText: MessageDescriptor
  consentUrl: string
}

export function getConsentsInfoForUser(episodes: Episode[], isUserInternational: boolean): UserConsent[] {
  const userEpisodePrograms = [...new Set(episodes.map((episode) => episode.program_name))]

  return userEpisodePrograms.flatMap((name) => {
    const consents = Programs.getProgramConfigByName(name)?.consents ?? []
    return consents
      .filter(({ showConsentIf }) => showConsentIf({ isInternational: isUserInternational }))
      .map(({ consentText, consentUrl }) => ({ programName: name, consentText, consentUrl }))
  })
}

export function isValidCTPOCombination(ctpo?: Partial<ProviderProgramTaxonomy>): ctpo is ProviderProgramTaxonomy {
  const { clientele, treatment, partner, offering } = ctpo ?? {}
  if (!clientele || !treatment || !partner || !offering) {
    return false
  }

  const matchingPrograms = Programs.getProgramConfigsMatching(
    (program) =>
      program.treatmentOptionProperties?.programTaxonomyTreatmentOptions?.some(
        (taxonomy) =>
          taxonomy.clientele === clientele &&
          taxonomy.treatment === treatment &&
          taxonomy.partner === partner &&
          taxonomy.offering === offering,
      ) ?? false,
  )
  return matchingPrograms.length > 0
}

export function getTriageTreatmentOptionFromCTPO(ctpo: ProviderProgramTaxonomy): TREATMENT_OPTIONS_TYPE | undefined {
  const programsWithTaxonomy = Programs.getProgramConfigsMatching(
    (program) =>
      program.treatmentOptionProperties?.programTaxonomyTreatmentOptions != null &&
      program.treatmentOptionProperties?.associatedTriageTreatmentOptions != null,
  )
  for (const program of programsWithTaxonomy) {
    const treatmentOptions = program.treatmentOptionProperties!.associatedTriageTreatmentOptions
    const taxonomies = program.treatmentOptionProperties!.programTaxonomyTreatmentOptions!
    const taxonomyIdx = taxonomies.findIndex(
      (taxonomy) =>
        taxonomy.clientele === ctpo.clientele &&
        taxonomy.treatment === ctpo.treatment &&
        taxonomy.partner === ctpo.partner &&
        taxonomy.offering === ctpo.offering,
    )
    if (taxonomyIdx >= 0) {
      if (taxonomyIdx < treatmentOptions.length) {
        return treatmentOptions[taxonomyIdx]
      }
      // associated treatment options generally match 1:1 with taxonomies
      // but individual therapy has 3 taxonomies that map to a single treatment option.
      return treatmentOptions[0]
    }
  }
  return undefined
}

export function getListOfTreatmentOptionsFromProviderTaxonomy(
  taxonomy?: ProviderProgramTaxonomy[],
): TREATMENT_OPTIONS_TYPE[] {
  if (!taxonomy) {
    return []
  }

  return taxonomy
    .map((item) => getTriageTreatmentOptionFromCTPO(item))
    .filter((option): option is TREATMENT_OPTIONS_TYPE => option !== undefined)
}

export function isCustomerProgramSelfPayEligible(customerPropertyId: string | undefined): boolean {
  return Programs.getProgramsByCustomerPropertyId(customerPropertyId).some((program) => program.selfPayEligible)
}
